import { inject, Injectable } from '@angular/core';
import {
  clearLegacyAuthStorage,
  clearStoredAuthSession,
  readStoredAuthSession,
  StoredAuthSession,
  writeStoredAuthSession
} from '@knowledgehook/auth-storage';
import { DateTime } from 'luxon';
import { Observable, of } from 'rxjs';
import { IAuthenticationDataService, LANGUAGE } from 'shared-components';

import { TranslationService } from 'app/core/internationalization/translation.service';
import { ISchoolAuthData } from '../models/school-auth-data';
import { IUserClaims } from '../models/user-claims';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationDataService implements IAuthenticationDataService {
  private readonly _translationService = inject(TranslationService);

  public isAuthorized(): Observable<boolean> {
    return of(this.isExistAccessToken());
  }

  public isExistAccessToken(): boolean {
    return !!this.getAccessToken();
  }

  public isTransient(): boolean | null {
    const session = this.readSession();

    return session ? !!session.isTransient : null;
  }

  public getUserLocale(): string {
    return this.readSession()?.localeId ?? this._translationService.getCurrentLanguage() ?? LANGUAGE.ENGLISH_CANADA;
  }

  public setUserLocale(localeId: string): void {
    if (!localeId) {
      localeId = LANGUAGE.ENGLISH_CANADA;
    }

    const session = this.readSession();
    if (!session) {
      return;
    }

    this.writeSession({ ...session, localeId });
  }

  public getAccessToken(): string | null {
    return this.readSession()?.token ?? null;
  }

  public setAccessToken(accessToken: string | null): this {
    const session = this.readSession();

    if (accessToken !== null && session) {
      this.writeSession({ ...session, token: accessToken });
    }
    else if (accessToken === null) {
      this.clearUserInfo();
    }

    return this;
  }

  public clearUserInfo(): void {
    clearStoredAuthSession();
    clearLegacyAuthStorage();
  }

  public setUserClaims(userClaims: IUserClaims | null): void {
    if (userClaims === null) {
      this.clearUserInfo();
      return;
    }

    const session = this.readSession();
    this.writeSession({
      authMethod: userClaims.authMethod,
      expiry: this.toIsoString(userClaims.expiry),
      isTransient: userClaims.isTransient,
      localeId: userClaims.localeId,
      roles: userClaims.roles ?? [],
      token: session?.token ?? '',
      userId: userClaims.userId,
      username: userClaims.userName
    });
  }

  public getUserClaims(): IUserClaims | null {
    const session = this.readSession();

    if (!session) {
      return null;
    }

    return this.sessionToUserClaims(session);
  }

  public getUserId(): string | null {
    return this.readSession()?.userId ?? null;
  }

  public getAuthMethod(): string | null {
    return this.readSession()?.authMethod ?? null;
  }

  public getSamlAuthData(): ISchoolAuthData | null {
    const session = this.readSession();

    if (!session) {
      return null;
    }

    return {
      AuthMethod: session.authMethod,
      Expiry: DateTime.fromISO(session.expiry),
      IsTransient: session.isTransient,
      LocaleId: session.localeId,
      Roles: session.roles,
      Token: session.token,
      UserId: session.userId,
      Username: session.username
    };
  }

  private readSession(): StoredAuthSession | null {
    return readStoredAuthSession();
  }

  private writeSession(session: StoredAuthSession): void {
    writeStoredAuthSession(session);
    clearLegacyAuthStorage();
  }

  private sessionToUserClaims(session: StoredAuthSession): IUserClaims {
    return {
      authMethod: session.authMethod ?? '',
      expiry: DateTime.fromISO(session.expiry),
      isTransient: !!session.isTransient,
      localeId: session.localeId ?? '',
      roles: session.roles,
      userId: session.userId,
      userName: session.username ?? ''
    };
  }

  private toIsoString(expiry: DateTime | string): string {
    if (typeof expiry === 'string') {
      return expiry;
    }

    return expiry.toISO() ?? '';
  }
}
